// src/components/chat/TenantSelector.tsx

import * as React from "react";
import { useChatSettings } from "../../contexts/ChatSettingsContext";
import { useAuth } from "../../contexts/AuthContext";
import { allowedTenantIdsForUser } from "../../utils/tenantSelectorAccess";
import { extractTenantId } from "../../utils/jwtUtils";
import { createLogger } from "../../utils/logger";

const log = createLogger("TenantSelector");

/**
 * Full catalogue of selectable tenants (the label source). Which of these a
 * given user may PREVIEW is decided per-user by allowedTenantIdsForUser().
 */
const TENANTS = [
  { id: "bdcc09da-cda0-4f61-8de5-f2223e737731", label: "Dandilion Tenant" },
  { id: "oilgas11-t111-1111-1111-3b552f79e23b", label: "O&G Tenant" },
  { id: "software-t111-1111-1111-bf647708e907", label: "Software Tenant" },
  { id: "7c0fac25-3682-4830-910c-ea8aa3853ad6", label: "Manufacturing Tenant" },
  { id: "adbc8019-8aa3-46e1-a416-f2e7473b96f3", label: "BHI (Prod C) Tenant" },
  { id: "99045356-6f33-44b3-b83e-21cab4f3179d", label: "BHI (Prod A) Tenant" },
  { id: "a430c977-2786-4a63-a50e-90b71e55d889", label: "Booking Tenant" },
  { id: "2fd6bcd6-9e7f-4d45-952a-bf647708e907", label: "Catylex (Demo) Tenant" },
  { id: "bc2385ac-35dc-4e76-a8ac-3b552f79e23b", label: "GP Tenant" },
];

/**
 * Dropdown for previewing a different tenant's configuration.
 *
 * The user's OWN tenant is always the first option and the default — selecting
 * it (value "") applies no override, so their real, unmodified token is sent
 * (their standard experience, exactly as if the selector didn't exist). The
 * tenants they've been granted appear as additional preview options; picking one
 * is what triggers the JWT `tid` override. Follows the AgentSelector pattern.
 */
const TenantSelector: React.FC = () => {
  const { getTenantId, setTenantId } = useChatSettings();
  const { userInfo, accessToken } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // The user's real home tenant, from the original (un-rewritten) token.
  const homeTid = accessToken ? extractTenantId(accessToken) : null;

  // Options = the user's own tenant ("standard", value "" => no override) plus
  // the preview tenants they've been granted (their own is excluded to avoid a
  // duplicate entry).
  const options = React.useMemo(() => {
    const allowed = allowedTenantIdsForUser(userInfo?.email);
    const previews = (
      allowed === "all" ? TENANTS : TENANTS.filter((t) => allowed.includes(t.id))
    ).filter((t) => t.id !== homeTid);
    const homeCatalog = TENANTS.find((t) => t.id === homeTid);
    const homeOption = {
      id: "",
      label: homeCatalog ? homeCatalog.label : "Default tenant",
    };
    return [homeOption, ...previews];
  }, [userInfo?.email, homeTid]);

  // "" (the default) means "no override" → the user's own tenant.
  const selectedId = getTenantId();
  const currentOption = options.find((o) => o.id === selectedId) || options[0];

  // Click-outside detection to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuOpen]);

  // Only the user's own tenant, nothing to preview → no selector. (After the
  // hooks above, to keep hook order stable across renders.)
  if (options.length <= 1) return null;

  const handleSelectTenant = (tenantId: string) => {
    log.info("Tenant selected", { previousTenant: selectedId, newTenant: tenantId });
    setTenantId(tenantId);
    setIsMenuOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger button */}
      <div
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          cursor: "pointer",
          userSelect: "none",
          fontFamily: "'Segoe UI', sans-serif",
          fontSize: "12px",
          color: "#666",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
        }}
        title={`Current tenant: ${currentOption.label}`}
      >
        <span>{currentOption.label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="#999"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dropdown menu */}
      {isMenuOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "0",
            marginBottom: "4px",
            backgroundColor: "white",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            minWidth: "160px",
            zIndex: 1000,
            overflow: "hidden",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {options.map((option, index) => {
            const isSelected = option.id === currentOption.id;
            return (
              <button
                key={option.id || "home"}
                onClick={() => handleSelectTenant(option.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#333",
                  fontWeight: isSelected ? 500 : 400,
                  borderBottom: index < options.length - 1 ? "1px solid #f0f0f0" : "none",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                }}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <polyline
                      points="20,6 9,17 4,12"
                      stroke="#333"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TenantSelector;
